import { BankOutlined, CloseCircleOutlined, EditOutlined, UploadOutlined } from '@ant-design/icons';
import { Alert, Avatar, Col, Form, Input, Modal, Row, Select, Spin, Tooltip, Upload, message } from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import defaultCover from '../../assets/images/company-default-cover.jpg';
import { TADatePicker, TASelect } from '../../components/dashboard/styles';
import { PhoneInput } from '../../components/phone-input/phone-input';
import { ButtonType, TAButton, TAInput } from '../../components/widgets';
import patterns from '../../config/patterns';
import CompanyIndustries from '../../constants/company-industries';
import { countriesWithStates } from '../../constants/country-states';
import { CompanySizes } from '../../enums/company-sizes';
import { CompanyTypes } from '../../enums/company-types';
import authService from '../../services/auth.service';
import companyService from '../../services/company.service';
import jobsService from '../../services/jobs.service';
import { CompanyScale } from '../../types/company-scale';
import { getBase64 } from '../../utils/files';
import { camelCaseToWord } from '../../utils/strings';
const { phoneNumberPattern, faxNumberPattern } = patterns;
let countries = Object.keys(countriesWithStates);

export const EditCompanyProfile: React.FC<any> = ({ backOffice, setUser }) => {
	const [selectedCompany, setSelectedCompany] = useState<any>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [modified, setModified] = useState<boolean>(false);
	const [coverPreviewVisible, setCoverPreviewVisible] = useState<boolean>(false);
	const [logoPreviewVisible, setLogoPreviewVisible] = useState<boolean>(false);
	const [selectedCountry, setSelectedCountry] = useState<any>(null);
	const [error, setError] = useState<any>(null);
	const [logoPreview, setLogoPreview] = useState<any>();
	const [coverPreview, setCoverPreview] = useState<any>();

	const [form] = Form.useForm();

	let { companyId } = useParams();

	const mountLogic = async () => {
		let company;
		try {
			company = backOffice ? await jobsService.getCompany(companyId) : await authService.getCompanyProfile();
			if (!company) throw new Error('Error while fetching data, Please try again later.');
			setSelectedCompany(company);
		} catch (e: any) {
			console.error(e);
			message.error(e.message);
			setLoading(false);
			setError(e.message);
			return;
		}

		company.foundationDate = moment(company.foundationDate);
		let [phoneCode, phoneNumber] = company.phone ? company.phone.split('-') : ['20', ''];
		company.country = company.location?.country;
		company.city = company.location?.city;
		company.area = company.location?.area;
		company.website = company.onlinePresence?.website;
		company.linkedinProfile = company.onlinePresence?.linkedinProfile;
		company.facebookProfile = company.onlinePresence?.facebookProfile;
		company.twitterProfile = company.onlinePresence?.twitterProfile;
		delete company.onlinePresence;
		delete company.location;
		setSelectedCountry(company.location?.country);
		setLoading(false);
		setLogoPreview(company.logo);
		setCoverPreview(company.cover);

		form.setFieldsValue({
			...company,
			phone: {
				code: phoneCode,
				phone: phoneNumber
			}
		});
	};

	useEffect(() => {
		mountLogic();
	}, []);

	const onFinish = async values => {
		setLoading(true);
		if (logoPreview) values.logo = logoPreview;
		if (coverPreview) values.cover = coverPreview;
		if (values.phone?.phone) values.phone = `${values.phone.code}-${values.phone.phone}`;
		let resp;
		let fd = new FormData();
		Object.keys(values).forEach(k => values[k] && fd.append(k, values[k]));
		try {
			resp = backOffice
				? await companyService.updateCompany(fd, companyId)
				: await authService.updateCompanyProfile(fd, companyId);
		} catch (e: any) {
			setError(e.message);
			setLoading(false);
			message.error(e.message);
			return;
		}
		message.success('Profile updated successfully!');
		if (!backOffice) setUser && setUser(resp);
		setModified(false);
		setLoading(false);
	};

	const onLogoUploadChange = async info => {
		const imageUrl = await getBase64(info.file);
		setLogoPreview(imageUrl);
	};

	const onCoverUploadChange = async info => {
		const imageUrl = await getBase64(info.file);
		setCoverPreview(imageUrl);
	};

	return (
		<div className={`ta-form md mx-auto my-3`}>
			<Row gutter={0} className="mb-4">
				<Col xs={{ span: 22 }} className="text-start page-label">
					Edit Company information
				</Col>
			</Row>
			{error && <Alert message="Error While fetching data" description={error} type="error" closable />}

			<Spin tip="please wait..." spinning={loading}>
				<Form layout="vertical" form={form} onFinish={onFinish} className="mb-4 box ta-form">
					<div className="profile-images-container">
						<div className="cover-window" onClick={e => setCoverPreviewVisible(true)}>
							<img className="photo" src={coverPreview || defaultCover} />
							<span className="overlay-inner-text">
								<EditOutlined />
							</span>
						</div>

						<div className="avatar-window sm" onClick={e => setLogoPreviewVisible(true)}>
							{logoPreview ? (
								<img className="photo" src={logoPreview} alt="logo" />
							) : (
								<Avatar shape="square" icon={<BankOutlined />} className="photo photo-icon" />
							)}
							<span className="overlay-inner-text">
								<EditOutlined />
							</span>
						</div>

						<Modal
							visible={logoPreviewVisible}
							footer={[
								<TAButton btntype={ButtonType.SECONDARY} onClick={() => setLogoPreviewVisible(false)}>
									Cancel
								</TAButton>,
								<TAButton
									btntype={ButtonType.PRIMARY}
									onClick={() => setLogoPreview(null)}
									className="d-inline-flex align-items-center me-2"
								>
									<CloseCircleOutlined />
									<span>Delete</span>
								</TAButton>,
								<Upload
									name="Avatar"
									beforeUpload={() => false}
									fileList={[]}
									onChange={onLogoUploadChange}
								>
									<Tooltip title="You can upload a .jpg, .png, or .gif images with max size of 5MB.">
										<TAButton btntype={ButtonType.PRIMARY} className="d-flex align-items-center">
											<UploadOutlined /> <span>Upload</span>
										</TAButton>
									</Tooltip>
								</Upload>
							]}
							onCancel={() => setLogoPreviewVisible(false)}
						>
							{logoPreview ? (
								<img alt="example" className="photo-modal-view" src={logoPreview} />
							) : (
								<Avatar className="photo-modal-view" shape="square" icon={<BankOutlined />} />
							)}
						</Modal>
						<Modal
							visible={coverPreviewVisible}
							footer={[
								<TAButton btntype={ButtonType.SECONDARY} onClick={() => setCoverPreviewVisible(false)}>
									Cancel
								</TAButton>,
								<TAButton
									btntype={ButtonType.PRIMARY}
									onClick={() => setCoverPreview(null)}
									className="d-inline-flex align-items-center me-2"
								>
									<CloseCircleOutlined />
									<span>Delete</span>
								</TAButton>,
								<Upload
									name="Avatar"
									beforeUpload={() => false}
									fileList={[]}
									onChange={onCoverUploadChange}
								>
									<Tooltip title="You can upload a .jpg, .png, or .gif images with max size of 5MB.">
										<TAButton btntype={ButtonType.PRIMARY} className="d-flex align-items-center">
											<UploadOutlined /> <span>Upload</span>
										</TAButton>
									</Tooltip>
								</Upload>
							]}
							width="80%"
							style={{ height: '300px' }}
							bodyStyle={{ padding: '2px' }}
							onCancel={() => setCoverPreviewVisible(false)}
						>
							<img className="photo-modal-view cover" src={coverPreview || defaultCover} alt="cover" />
						</Modal>
					</div>

					<div className="form-fields-container">
						<fieldset className="ta-form-section">
							<legend className="ta-form-section-title">Company Information</legend>

							<Form.Item
								label="Name"
								name="name"
								help={
									<p className="text-faded">
										{backOffice
											? ' Be Careful Here!'
											: "You can't change the company name, please contact our support for help"}
									</p>
								}
								rules={[{ required: true, message: 'Please input company name.' }]}
							>
								<Input disabled={!backOffice} />
							</Form.Item>

							<Form.Item
								label="Country"
								name="country"
								rules={[{ required: true, message: 'Please add company country.' }]}
							>
								<TASelect
									onChange={e => {
										const countryEvent = e as string;
										if (e) setSelectedCountry(countryEvent);
									}}
									placeholder="Country"
									showSearch
								>
									{countries.map(country => (
										<Select.Option value={country} key={country}>
											{country}
										</Select.Option>
									))}
								</TASelect>
							</Form.Item>

							<Form.Item
								label="City"
								name="city"
								rules={[{ required: true, message: 'Please add company city.' }]}
							>
								<TASelect disabled={!selectedCountry} placeholder="City" showSearch>
									{selectedCountry
										? countriesWithStates[selectedCountry].map(state => (
												<Select.Option value={state} key={state}>
													{state}
												</Select.Option>
										  ))
										: null}
								</TASelect>
							</Form.Item>

							<Form.Item
								label="Area"
								name="area"
								rules={[{ required: true, message: 'Please add company area.' }]}
							>
								<TAInput placeholder="Please make it detailed" />
							</Form.Item>

							<Form.Item
								label="Founded At"
								name="foundationDate"
								rules={[
									{
										validator: (rule, value, callback) => {
											if (value.toDate().getTime() > Date.now()) {
												callback("Can't enter future data");
											}
											return callback();
										}
									}
								]}
							>
								<TADatePicker />
							</Form.Item>

							<Form.Item label="Company Type" name="type">
								<TASelect>
									{Object.values(CompanyTypes).map(type => (
										<Select.Option value={type} key={type}>
											{type}
										</Select.Option>
									))}
								</TASelect>
							</Form.Item>

							<Form.Item label="Company Size" name="size">
								<TASelect>
									{Object.values(CompanySizes).map(s => (
										<Select.Option value={s} key={s}>
											{s} Employees
										</Select.Option>
									))}
								</TASelect>
							</Form.Item>

							<Form.Item label="Company Scale" name="scale">
								<TASelect>
									{Object.values(CompanyScale).map(s => (
										<Select.Option value={s} key={s}>
											{camelCaseToWord(s)}
										</Select.Option>
									))}
								</TASelect>
							</Form.Item>

							<Form.Item label="Industry" name="industry">
								<TASelect
									mode="multiple"
									showSearch
									optionFilterProp="children"
									tokenSeparators={[',']}
									placeholder="e.g. Recruitment, Marketing"
								>
									{CompanyIndustries.map(industry => (
										<Select.Option value={industry}>{industry}</Select.Option>
									))}
								</TASelect>
							</Form.Item>
						</fieldset>

						<fieldset className="ta-form-section">
							<legend className="ta-form-section-title">Contacts</legend>

							<Form.Item
								label="Email"
								name="email"
								rules={[{ type: 'email', message: 'Not valid email' }]}
							>
								<TAInput />
							</Form.Item>

							<Form.Item
								label="Phone"
								name="phone"
								rules={[
									{
										validator: (rule, value, callback) => {
											if (!value || !value.code || !value.phone)
												callback('Must enter phone number and code');
											else if (!phoneNumberPattern.test(value.phone))
												callback('Not valid phone number');
											else callback();
										}
									}
								]}
							>
								<PhoneInput />
							</Form.Item>

							<Form.Item
								label="Fax"
								name="fax"
								rules={[{ pattern: faxNumberPattern, message: 'Not valid fax number' }]}
							>
								<TAInput />
							</Form.Item>
						</fieldset>

						<fieldset className="ta-form-section">
							<legend className="ta-form-section-title">Profile</legend>

							<Form.Item
								label="Bio"
								name="bio"
								rules={[{ max: 2000, message: `maximum 2000 characters` }]}
							>
								<Input.TextArea rows={6} />
							</Form.Item>
						</fieldset>

						<fieldset className="ta-form-section">
							<legend className="ta-form-section-title">Online Presence</legend>

							<Form.Item
								label="Website"
								name="website"
								rules={[{ type: 'url', message: 'Not valid url' }]}
							>
								<TAInput />
							</Form.Item>

							<Form.Item
								label="Linkedin"
								name="linkedinProfile"
								rules={[{ type: 'url', message: 'Not valid url' }]}
							>
								<TAInput />
							</Form.Item>

							<Form.Item
								label="Facebook"
								name="facebookProfile"
								rules={[{ type: 'url', message: 'Not valid url' }]}
							>
								<TAInput />
							</Form.Item>

							<Form.Item
								label="Twitter"
								name="twitterProfile"
								rules={[{ type: 'url', message: 'Not valid url' }]}
							>
								<TAInput />
							</Form.Item>
						</fieldset>
					</div>
					<Form.Item>
						<TAButton
							btntype={ButtonType.PRIMARY}
							htmlType="submit"
							className="w-100 mt-4"
							style={{ height: '44px' }}
						>
							Save
						</TAButton>
					</Form.Item>
				</Form>
			</Spin>
			{/* <Prompt when={modified} message="You have unsaved fields. Are you sure you want to leave this page?" /> */}
		</div>
	);
};
